
import { Brain, Zap, BarChart, Brain as BrainIcon, Stars } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Hero = () => {
  return (
    <section className="relative py-16 md:py-24 px-4 md:px-8 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-brain-purple/5 to-brain-teal/5"></div>
        <motion.div 
          className="absolute top-20 left-1/4 w-64 h-64 rounded-full bg-brain-purple/5"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }} 
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute bottom-20 right-1/4 w-80 h-80 rounded-full bg-brain-teal/5"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.4, 0.2]
          }} 
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        />
      </div>
      
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-10 md:gap-16">
          <motion.div 
            className="md:w-1/2 z-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="inline-flex items-center px-3 py-1.5 mb-6 rounded-full bg-gradient-to-r from-brain-purple/10 to-brain-teal/10 border border-brain-light-purple/20 text-brain-purple"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Zap size={16} className="mr-2" />
              <span className="text-sm font-medium">Scientifically proven brain games</span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <span className="bg-gradient-to-r from-brain-purple via-brain-vivid-purple to-brain-teal bg-clip-text text-transparent">
                Boost Your Brain Power
              </span>
              <br /> 
              <span className="text-foreground">
                Through Fun Games
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-muted-foreground mb-8 max-w-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Challenge your mind with our collection of games designed to improve 
              memory, focus, problem-solving abilities, and cognitive flexibility.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-brain-purple to-brain-vivid-purple hover:opacity-90 shadow-md text-white"
                asChild
              >
                <Link to="/games">
                  Start Playing
                </Link>
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-brain-light-purple/30 hover:bg-brain-purple/5 hover:border-brain-purple/50"
                asChild
              >
                <Link to="/about">
                  Learn More
                </Link>
              </Button>
            </motion.div>
            
            <motion.div 
              className="flex items-center gap-6 mt-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="flex flex-col items-center">
                <div className="text-brain-purple font-bold text-2xl">30+</div>
                <div className="text-xs text-muted-foreground">Brain Games</div>
              </div>
              <div className="h-10 w-px bg-brain-light-purple/30"></div>
              <div className="flex flex-col items-center">
                <div className="text-brain-purple font-bold text-2xl">5M+</div>
                <div className="text-xs text-muted-foreground">Users Trained</div>
              </div>
              <div className="h-10 w-px bg-brain-light-purple/30"></div>
              <div className="flex flex-col items-center">
                <div className="text-brain-purple font-bold text-2xl">95%</div>
                <div className="text-xs text-muted-foreground">Satisfaction</div>
              </div>
            </motion.div>
          </motion.div>
          
          <motion.div 
            className="md:w-1/2 flex justify-center relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="relative w-[320px] h-[320px] md:w-[420px] md:h-[420px]">
              {/* Animated circular elements */}
              <motion.div 
                className="absolute inset-0 rounded-full bg-gradient-to-br from-brain-purple/20 to-brain-teal/20 border border-white/50"
                animate={{ 
                  scale: [1, 1.03, 1],
                  rotate: [0, 10, 0, -10, 0],
                }}
                transition={{ 
                  duration: 12,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              <motion.div 
                className="absolute inset-12 rounded-full bg-gradient-to-tr from-brain-purple/20 to-brain-teal/20 border border-white/40"
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.05, 1],
                }}
                transition={{ 
                  rotate: {
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear"
                  },
                  scale: {
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }}
              />
              
              <motion.div 
                className="absolute inset-24 rounded-full bg-gradient-to-bl from-brain-purple/20 to-brain-teal/20 border border-white/30"
                animate={{ 
                  rotate: [360, 0],
                }}
                transition={{ 
                  duration: 40,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              
              <motion.div 
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                animate={{ 
                  scale: [1, 1.1, 1],
                }}
                transition={{ 
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-brain-purple/30 blur-xl"></div>
                  <div className="relative z-10">
                    <Brain size={120} className="text-brain-purple" strokeWidth={1.25} />
                  </div>
                </div>
              </motion.div>
              
              {/* Feature icons floating around */}
              <motion.div
                className="absolute top-10 left-[45%] bg-white p-3 rounded-lg shadow-lg"
                animate={{ 
                  y: [0, -15, 0],
                  x: [0, 10, 0],
                  rotate: [0, 5, 0, -5, 0]
                }}
                transition={{ 
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <BarChart size={28} className="text-brain-purple" />
              </motion.div>
              
              <motion.div
                className="absolute bottom-40 left-10 bg-white p-3 rounded-lg shadow-lg"
                animate={{ 
                  y: [0, 15, 0],
                  x: [0, -10, 0],
                  rotate: [0, -5, 0, 5, 0]
                }}
                transition={{ 
                  duration: 7,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              >
                <BrainIcon size={28} className="text-brain-teal" />
              </motion.div>
              
              <motion.div
                className="absolute bottom-20 right-20 bg-white p-3 rounded-lg shadow-lg"
                animate={{ 
                  y: [0, 20, 0],
                  x: [0, 5, 0],
                  rotate: [0, 8, 0, -8, 0]
                }}
                transition={{ 
                  duration: 9,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2
                }}
              >
                <Stars size={28} className="text-brain-vivid-purple" />
              </motion.div>
            </div>
            
            {/* Neural network connections */}
            <motion.svg 
              className="absolute inset-0 w-full h-full opacity-20 z-0" 
              viewBox="0 0 420 420" 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 2, delay: 1.5 }}
            >
              {/* Neural pathways */}
              <motion.path 
                d="M210,50 C100,120 270,180 140,260" 
                stroke="#9b87f5" 
                strokeWidth="1" 
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, delay: 1 }}
              />
              <motion.path 
                d="M210,50 C320,150 100,200 280,260" 
                stroke="#9b87f5" 
                strokeWidth="1" 
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2.5, delay: 1.5 }}
              />
              <motion.path 
                d="M140,260 C240,300 290,180 280,260" 
                stroke="#9b87f5" 
                strokeWidth="1" 
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, delay: 2 }}
              />
              <motion.path 
                d="M140,260 C60,330 230,350 210,370" 
                stroke="#9b87f5" 
                strokeWidth="1" 
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, delay: 2.3 }}
              />
              <motion.path 
                d="M280,260 C330,330 250,340 210,370" 
                stroke="#9b87f5" 
                strokeWidth="1" 
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2.2, delay: 2.6 }}
              />
            </motion.svg>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
